import type { ReactNode } from 'react'
import { radius } from './primitives/shape'

/**
 * A full-screen sheet over the workspace, with a title and one scrolling body.
 *
 * Two surfaces use it, Lessons and Setup, and both are the same shape: a
 * document you read, not a form with sections. `Settings` keeps its own shell
 * because it is a different thing, a sidebar of eight panes, and folding one
 * into the other would mean a component with a mode.
 *
 * Compact gives the sheet the whole screen. There is no "outside the dialog"
 * worth showing on a phone, and a 20px frame around a reading surface is 40px
 * of scrolling nobody asked for. The rule is copied from `Settings` rather than
 * re-decided, because two dialogs that frame themselves differently on the same
 * phone read as two applications.
 */
export function Overlay({
  title,
  subtitle,
  compact,
  onClose,
  children,
}: {
  title: string
  subtitle?: ReactNode
  compact: boolean
  onClose: () => void
  children: ReactNode
}) {
  return (
    <div
      role="dialog"
      aria-label={title}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 40,
        background: 'rgba(0,0,0,.7)',
        backdropFilter: 'blur(14px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: compact ? 0 : 20,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: compact ? '100%' : 'min(920px,100%)',
          height: compact ? '100%' : 'min(660px,100%)',
          background: 'var(--ground-raised)',
          border: compact ? 'none' : '1px solid var(--border-strong)',
          borderRadius: compact ? 0 : radius.xl,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: compact ? 'none' : 'var(--shadow-overlay)',
          paddingTop: compact ? 'var(--safe-top)' : 0,
          paddingLeft: compact ? 'var(--safe-left)' : 0,
          paddingRight: compact ? 'var(--safe-right)' : 0,
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            padding: '20px 22px 14px',
            borderBottom: '1px solid var(--border)',
            flex: 'none',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, letterSpacing: '-.02em' }}>{title}</h2>
          {subtitle && (
            <span style={{ font: '400 11px var(--font-machine)', color: 'var(--text-faint)' }}>{subtitle}</span>
          )}
          <div style={{ flex: 1 }} />
          <button
            onClick={onClose}
            aria-label={`Close ${title.toLowerCase()}`}
            className="sp-tap"
            style={{
              width: 28,
              height: 28,
              borderRadius: radius.xs,
              background: 'var(--panel-strong)',
              cursor: 'pointer',
              flex: 'none',
            }}
          >
            ✕
          </button>
        </header>

        <div
          className="sp-settings-pane"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: compact ? '18px 18px calc(26px + var(--safe-bottom))' : '20px 22px 26px',
            overscrollBehaviorY: 'contain',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  )
}
